import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getClientLog, getClientLogView, getDeviceLogView } from "../../lib/api";
import { PageLoader, Spinner } from "../../components/ui/spinner";
import { formatStartedWithDuration, downloadEventsCsv, EventsPagination, eventCount, deriveEventsView } from "./sessionLogShared";

export default function ClientLogDetailPage() {
  const { clientId, sessionId } = useParams();
  const navigate = useNavigate();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logView, setLogView] = useState("client");
  const [deviceFirst, setDeviceFirst] = useState(false);

  // Real client-log-view / device-log-view events for this session_id — neither
  // endpoint has page params, so they return everything and pagination happens
  // client-side. Same two endpoints DeviceLogDetailPage uses, just opened from
  // the opposite lane.
  const [realClientEvents, setRealClientEvents] = useState(null);
  const [clientEventsLoading, setClientEventsLoading] = useState(true);
  const [realDeviceEvents, setRealDeviceEvents] = useState(null);
  const [deviceEventsLoading, setDeviceEventsLoading] = useState(true);
  const [clientDeviceId, setClientDeviceId] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    getClientLog(clientId, sessionId)
      .then(setLog)
      .finally(() => setLoading(false));
  }, [clientId, sessionId]);

  useEffect(() => {
    setClientEventsLoading(true);
    setPage(1);
    getClientLogView(sessionId)
      .then((data) => {
        setRealClientEvents(data?.events?.length ? data.events : null);
        setClientDeviceId(data?.deviceId || "");
      })
      .finally(() => setClientEventsLoading(false));
  }, [sessionId]);

  useEffect(() => {
    setDeviceEventsLoading(true);
    setPage(1);
    getDeviceLogView(sessionId)
      .then((data) => setRealDeviceEvents(data?.events?.length ? data.events : null))
      .finally(() => setDeviceEventsLoading(false));
  }, [sessionId]);

  function selectLogView(view) {
    setLogView(view);
    setPage(1);
  }

  function downloadLogCsv() {
    downloadEventsCsv(`${logView}_${sessionId}.csv`, allEvents);
  }

  function downloadBundle() {
    downloadEventsCsv(`client_${sessionId}.csv`, realClientEvents || log.clientEvents);
    downloadEventsCsv(`device_${sessionId}.csv`, realDeviceEvents || log.deviceEvents);
  }

  if (loading) return <PageLoader />;

  if (!log) {
    return (
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate(`/sessions/clients?client=${clientId}`)}
          className="self-start flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors"
        >
          &#8592; {clientId} logs
        </button>
        <p className="text-sm text-slate-400 py-6 text-center">Log {sessionId} was not found.</p>
      </div>
    );
  }

  const primaryLabel = deviceFirst ? "Device Log" : "Client Log";
  const clientEventsCount = eventCount(realClientEvents, log.bundle.clientEvents);
  const deviceEventsCount = eventCount(realDeviceEvents, log.bundle.deviceEvents);
  const { usingRealLog, allEvents, totalPages, currentPage, events, isLoading: isCurrentViewLoading } =
    deriveEventsView({
      view: logView,
      page,
      lanes: {
        client: { real: realClientEvents, fallback: log.clientEvents, loading: clientEventsLoading },
        device: { real: realDeviceEvents, fallback: log.deviceEvents, loading: deviceEventsLoading },
      },
    });

  return (
    <div className="flex flex-col gap-0">
      {/* Breadcrumb / back */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => navigate(`/sessions/clients?client=${clientId}`)}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors"
        >
          &#8592; {clientId} logs
        </button>
        <button
          onClick={() => setDeviceFirst((v) => !v)}
          className="text-xs font-semibold text-[#08148C] hover:underline"
        >
          &#8646; Come from {deviceFirst ? "Client" : "Device"} instead?
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Opened from Client <span className="font-semibold text-slate-700">{clientId}</span> &rarr; primary &middot; {primaryLabel} &middot;
        {" "}counterpart &middot; Device <span className="font-semibold text-slate-700">{log.device}</span> log
      </p>

      {/* Title row */}
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Activity Log {sessionId}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-blue-200 bg-blue-50 text-[#08148C]">
              {log.channel} channel
            </span>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
              Client {clientId}
            </span>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
              Device {log.device}
            </span>
            {log.patientId && (
              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                Patient {log.patientId} [de-identified]
              </span>
            )}
            {log.verified && (
              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-600">
                &#10003; Verified
              </span>
            )}
          </div>
        </div>
        <button
          onClick={downloadBundle}
          title="Download both the client log and device log for this session as separate CSV files."
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-200 text-slate-600 rounded-lg px-3 py-2 hover:border-[#08148C] hover:text-[#08148C] transition-colors shrink-0"
        >
          &#8659; Download bundle (this log + counterpart)
        </button>
      </div>

      {/* Session info strip */}
      <div className="bg-[#08148C] rounded-xl px-6 py-4 mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-6">
          {[
            { label: "PORTAL SESSION ID", value: log.portalSessionId },
            { label: "CLIENT SESSION ID", value: clientDeviceId || "-" },
            { label: "CHANNEL", value: log.channel },
            { label: "STARTED · DUR", value: formatStartedWithDuration(log.startedAt, log.durationMin) },
            { label: "BUNDLE", value: `Client ${clientEventsCount} · Device ${deviceEventsCount}` },
          ].map((item) => (
            <div key={item.label} className="min-w-0">
              <p className="text-[11px] font-semibold text-blue-300 uppercase tracking-wider mb-1">{item.label}</p>
              <p className="text-sm font-bold text-white truncate">{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Session summary */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 mb-5">
        <p className="text-xs font-bold text-[#08148C] uppercase tracking-widest mb-2">Session Summary</p>
        <p className="text-sm text-slate-600">{log.sessionSummary || "Session summary is not available for this log yet."}</p>
      </div>

      {/* Connected logs · this bundle */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-[#08148C] uppercase tracking-widest">Connected Logs &middot; This Bundle</p>
          <span className="text-[11px] text-slate-400 italic">correlated on clientSessionId</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800">Client Activity Log</p>
              <p className="text-xs text-slate-500 truncate">{clientId} &middot; {clientEventsCount} events &middot; client_{sessionId}.jsonl</p>
            </div>
          </div>
          <div className="border border-purple-200 bg-purple-50/40 rounded-lg p-3.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800">Device Activity Log &mdash; correlated counterpart</p>
              <p className="text-xs text-slate-500 truncate">{log.device} &middot; {deviceEventsCount} events &middot; device_{sessionId}.jsonl</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={downloadBundle}
            title="Download both the client log and device log for this session as separate CSV files."
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-slate-100 text-slate-600 rounded-lg px-3 py-1.5 hover:bg-[#08148C] hover:text-white transition-colors shrink-0"
          >
            &#8659; Download bundle (2 CSV files)
          </button>
          <p className="text-xs text-slate-400">
            The bundle is just the two logs from this session &mdash; the log you opened + its opposite lane counterpart.
            Open from a device and the Device Log becomes primary.
          </p>
        </div>
      </div>

      {/* Log view */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
          <p className="text-xs font-bold text-[#08148C] uppercase tracking-widest">
            Log View
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => selectLogView("client")}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                logView === "client" ? "bg-[#08148C] text-white border-[#08148C]" : "bg-white text-slate-600 border-slate-300 hover:border-[#08148C]"
              }`}
            >
              Client Log
            </button>
            <button
              onClick={() => selectLogView("device")}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                logView === "device" ? "bg-[#08148C] text-white border-[#08148C]" : "bg-white text-slate-600 border-slate-300 hover:border-[#08148C]"
              }`}
            >
              Device Log
            </button>
            <button
              onClick={downloadLogCsv}
              title="Download this log as a read-only CSV file."
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-slate-200 text-slate-600 rounded-lg px-3 py-1.5 hover:border-[#08148C] hover:text-[#08148C] transition-colors ml-2"
            >
              &#8659; Download log (read-only)
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-400 px-5 pt-3">
          {logView === "client"
            ? `Client Log — operator/app events for this session. You opened from the client, so this lane shows first. Switch to Device Log for ${log.device}'s events.`
            : `Device Log — pump events for this session, from ${log.device}.`}
          {!isCurrentViewLoading && usingRealLog && ` · ${allEvents.length} events`}
        </p>
        {isCurrentViewLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <Spinner size="lg" />
            <p className="text-xs text-slate-400">
              Loading {logView === "client" ? "client" : "device"} log&hellip; this can take a moment for large logs.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto px-5 pb-5 pt-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {["TIMESTAMP UTC", "EVENT", "DETAIL"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {events.map((e, i) => (
                  <tr key={i} /* NOSONAR S6479: raw backend rows carry no guaranteed unique id; a composite key risks duplicates. */ className="hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500 whitespace-nowrap">{e.ts}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{e.event}</td>
                    <td className="px-3 py-2.5 text-slate-600">{e.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isCurrentViewLoading && usingRealLog && (
          <EventsPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={allEvents.length}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
